import * as React from 'react';

import type { Table as TanstackTable } from '@tanstack/react-table';

import { isSelectColumn } from '../utils/column-width-style';

interface UseFilledColumnWidthsParams<TData> {
  table: TanstackTable<TData>;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  /** Set when the table is resizable; the whole feature is off otherwise. */
  tableId?: string;
  /** How many trailing columns share the remaining space equally. */
  fillLastColumns: number;
}

interface FilledColumnWidths {
  /** Explicit widths for the trailing columns, or undefined when the table is not resizable. */
  filledColumnWidths?: Map<string, number>;
  /** Table width: at least the container so colSpan rows (loading/error) span the full width. */
  tableWidth?: number;
}

/**
 * Resizable tables: tracks the container width and gives the last `fillLastColumns` columns the
 * remaining space, so resizing one column only ever moves the trailing ones. On the very first
 * measure, and only when no sizing was persisted, extra width is spread across all columns.
 */
export function useFilledColumnWidths<TData>({
  table,
  scrollContainerRef,
  tableId,
  fillLastColumns,
}: UseFilledColumnWidthsParams<TData>): FilledColumnWidths {
  const [containerWidth, setContainerWidth] = React.useState(0);
  const hasDistributedInitialWidth = React.useRef(false);

  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !tableId) return;

    const measure = () => {
      const width = container.clientWidth;
      setContainerWidth(width);

      if (!hasDistributedInitialWidth.current && width > 0) {
        hasDistributedInitialWidth.current = true;
        distributeExtraWidthOnce(table, width);
      }
    };

    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [tableId, table, scrollContainerRef]);

  // Both states are read so the memo recomputes when sizes or visible columns change.
  const columnSizing = table.getState().columnSizing;
  const columnVisibility = table.getState().columnVisibility;
  const filledColumnWidths = React.useMemo(() => {
    if (!tableId || !containerWidth) return undefined;

    const visibleColumns = table.getVisibleLeafColumns();
    const fillCount = Math.min(fillLastColumns, visibleColumns.length);
    const filledColumns = visibleColumns.slice(-fillCount);
    const otherColumnsWidth = visibleColumns
      .slice(0, -fillCount)
      .reduce((sum, column) => sum + column.getSize(), 0);
    const perColumnWidth = (containerWidth - otherColumnsWidth) / fillCount;

    return new Map(
      filledColumns.map(column => [column.id, Math.max(perColumnWidth, column.getSize())])
    );
  }, [tableId, containerWidth, table, columnSizing, columnVisibility, fillLastColumns]);

  const tableWidth = tableId ? Math.max(table.getTotalSize(), containerWidth) : undefined;

  return { filledColumnWidths, tableWidth };
}

/** Spreads the container's spare width across every column except the fixed select column. */
function distributeExtraWidthOnce<TData>(table: TanstackTable<TData>, containerWidth: number) {
  const hasPersistedSizing = Object.keys(table.getState().columnSizing).length > 0;
  if (hasPersistedSizing) return;

  const visibleColumns = table.getVisibleLeafColumns();
  const totalDefaultWidth = visibleColumns.reduce((sum, column) => sum + column.getSize(), 0);
  if (containerWidth <= totalDefaultWidth) return;

  const extraPerColumn = (containerWidth - totalDefaultWidth) / visibleColumns.length;
  const sizing: Record<string, number> = {};
  for (const column of visibleColumns) {
    if (!isSelectColumn(column.id)) sizing[column.id] = column.getSize() + extraPerColumn;
  }
  table.setColumnSizing(sizing);
}
